import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import ConfirmDialog from '@/components/ConfirmDialog';
import api from '@/lib/api';
import { readPageCache, writePageCache } from '@/lib/pageCache';
import useRefreshOnFocus from '@/lib/useRefreshOnFocus';
import './Products.css';

const productCalculatorLabels = {
  offset_simple: 'Offset simple',
  revista: 'Revista',
  cuaderno: 'Cuaderno',
  facturas: 'Facturas',
};

const ProductsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const cachedProducts = readPageCache('products-list');
  const [products, setProducts] = useState(cachedProducts || []);
  const [loading, setLoading] = useState(!cachedProducts);
  const [error, setError] = useState('');
  const [duplicatingId, setDuplicatingId] = useState('');
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await api.get('/products');
      setProducts(data);
      writePageCache('products-list', data);
    } catch (err) {
      setError('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useRefreshOnFocus(loadProducts);

  const handleDelete = async () => {
    if (!deleteProduct) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/products/${deleteProduct.id}`);
      setProducts(current => current.filter(p => p.id !== deleteProduct.id));
      setDeleteProduct(null);
    } catch (err) {
      alert('Error al eliminar producto');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      setDuplicatingId(id);
      const { data } = await api.post(`/products/${id}/duplicate`);
      setProducts(current => [data, ...current]);
      navigate(`/productos/${data.id}/editar`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error al replicar producto');
    } finally {
      setDuplicatingId('');
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar activePath={location.pathname} />

      <main className="main-content">
        <div className="page-header-row">
          <div>
            <h1 data-testid="products-title">Productos</h1>
            <p className="dashboard-subtitle">
              Catalogo de productos configurados para presupuestar
            </p>
          </div>
          <button
            className="btn-primary-inline"
            onClick={() => navigate('/productos/nuevo')}
            data-testid="new-product-button"
          >
            + Nuevo Producto
          </button>
        </div>

        {error && <div className="login-error" data-testid="products-error">{error}</div>}

        {loading ? (
          <div className="empty-state">Cargando productos...</div>
        ) : products.length === 0 ? (
          <div className="card">
            <div className="empty-state" data-testid="empty-products">
              <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>No hay productos registrados</p>
              <p style={{ fontSize: '0.875rem', color: '#A0A0A0' }}>
                Comenza creando tu primer producto.
              </p>
            </div>
          </div>
        ) : (
          <div className="products-grid">
            {products.map(product => (
              <div key={product.id} className="product-card product-card-simple" data-testid={`product-card-${product.id}`}>
                <div className="product-image">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} />
                  ) : (
                    <div className="product-image-placeholder">
                      {product.name?.slice(0, 1)?.toUpperCase() || 'P'}
                    </div>
                  )}
                </div>

                <div className="product-card-header">
                  <h3 className="product-name">{product.name}</h3>
                  <div className="product-meta-badges">
                    <span>{product.production_type === 'offset' ? 'Offset' : product.production_type}</span>
                    <span>{productCalculatorLabels[product.calculator_type] || 'Offset simple'}</span>
                  </div>
                  {product.description && (
                    <p className="product-description">{product.description}</p>
                  )}
                </div>

                <div className="product-actions">
                  <Link
                    to={`/productos/${product.id}/editar`}
                    className="btn-secondary"
                    data-testid={`edit-product-${product.id}`}
                  >
                    Editar
                  </Link>
                  <button
                    className="btn-secondary"
                    onClick={() => handleDuplicate(product.id)}
                    disabled={duplicatingId === product.id}
                    data-testid={`duplicate-product-${product.id}`}
                  >
                    {duplicatingId === product.id ? 'Replicando...' : 'Replicar'}
                  </button>
                  <button
                    className="btn-danger"
                    onClick={() => setDeleteProduct(product)}
                    data-testid={`delete-product-${product.id}`}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <ConfirmDialog
          open={!!deleteProduct}
          title="Eliminar producto"
          message={deleteProduct ? `¿Estas seguro de eliminar el producto "${deleteProduct.name}"?` : ''}
          confirmLabel="Eliminar"
          loading={deleteLoading}
          onConfirm={handleDelete}
          onCancel={() => setDeleteProduct(null)}
        />
      </main>
    </div>
  );
};

export default ProductsList;
